import React, { useEffect, useState } from 'react';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import ToastHost, { showToast } from './components/Toast';
import VehicleModal from './components/VehicleModal';
import ChecklistModal from './components/ChecklistModal';
import Dashboard from './pages/Dashboard';
import Queue from './pages/Queue';
import Delivery from './pages/Delivery';
import AddVehicle from './pages/AddVehicle';
import Archive from './pages/Archive';
import Users from './pages/Users';
import CarModels from './pages/CarModels';
import { ROLE_NAV } from './helpers';
import { api } from './api';

const TOKEN_KEY = 'vf_token';
const USER_KEY  = 'vf_user';

function loadSession() {
  const token = localStorage.getItem(TOKEN_KEY);
  const userJson = localStorage.getItem(USER_KEY);
  if (!token || !userJson) return null;
  try { return { token, user: JSON.parse(userJson) }; }
  catch { return null; }
}

// First nav item for the role (becomes the default landing screen)
function defaultView(session) {
  if (!session) return { v: 'dash', l: 'Dashboard' };
  const items = ROLE_NAV[session.user.role] || ROLE_NAV.Sales;
  return { v: items[0].v, l: items[0].l };
}

function saveSession({ token, user }) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export default function App() {
  const [session, setSession]       = useState(() => loadSession());
  const [authView, setAuthView]     = useState('login'); // 'login' | 'signup'
  const initialDef                  = defaultView(loadSession());
  const [view, setView]             = useState(initialDef.v);
  const [pageLabel, setPageLabel]   = useState(initialDef.l);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Modals
  const [vehicleModalId, setVehicleModalId] = useState(null);
  const [checklistCtx, setChecklistCtx]     = useState(null);

  // Verify the token is still valid on mount; logout if it's not
  useEffect(() => {
    if (!session) return;
    api.me()
      .then(({ user }) => {
        // Refresh role/name from server in case Manager changed it
        const refreshed = { token: session.token, user };
        saveSession(refreshed);
        setSession(refreshed);
      })
      .catch(() => {
        clearSession();
        setSession(null);
        showToast('Session expired — please sign in again');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When role changes (or first login), pin to first nav item
  useEffect(() => {
    if (!session) return;
    const items = ROLE_NAV[session.user.role] || ROLE_NAV.Sales;
    if (!items.find((i) => i.v === view)) {
      setView(items[0].v);
      setPageLabel(items[0].l);
    }
  }, [session, view]);

  // Auto-refresh every 30s so all devices stay in sync without F5.
  // Skips a tick when the user is mid-edit (input/select/textarea focused,
  // or a modal is open) so we never clobber what they're typing.
  useEffect(() => {
    if (!session) return;
    const iv = setInterval(() => {
      const el = document.activeElement;
      const editing = el && ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName);
      const modalOpen = !!document.querySelector('.mo.open');
      if (!editing && !modalOpen) setRefreshKey((k) => k + 1);
    }, 30000);
    return () => clearInterval(iv);
  }, [session]);

  function login({ user, token }) {
    saveSession({ user, token });
    setSession({ user, token });
    const items = ROLE_NAV[user.role] || ROLE_NAV.Sales;
    setView(items[0].v);
    setPageLabel(items[0].l);
  }

  function logout() {
    clearSession();
    setSession(null);
    setAuthView('login');
  }

  function navigate(v, label) {
    setView(v);
    setPageLabel(label);
  }

  function bump() { setRefreshKey((k) => k + 1); }

  if (!session) {
    return (<>
      {authView === 'login'
        ? <Login  onLogin={login}     onSwitchToSignup={() => setAuthView('signup')} />
        : <Signup onSignedUp={(s) => { if (s.firstUser) login(s); else setAuthView('login'); }}
                  onSwitchToLogin={() => setAuthView('login')} />
      }
      <ToastHost />
    </>);
  }

  const commonProps = {
    session,
    refreshKey,
    onUpdate: (id) => setVehicleModalId(id),
    onOpenChecklist: (type, vehicle) => setChecklistCtx({ type, vehicle }),
    onAdd: () => navigate('add', 'Add Vehicle'),
    onCreated: () => { bump(); navigate('dash', 'Dashboard'); },
    onRefresh: bump,
  };

  // Adapter so existing pages reading session.role keep working
  const sessionForPages = { ...session, name: session.user.name, role: session.user.role };

  return (
    <>
      <div className="app-layout">
        <Sidebar
          session={{ name: session.user.name, role: session.user.role }}
          currentView={view}
          onNavigate={navigate}
          onLogout={logout}
          isMobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
        <div className="main">
          <Topbar pageLabel={pageLabel} onMenuOpen={() => setMobileMenuOpen(true)} />
          <div className="page-body">
            {view === 'dash'    && <Dashboard {...commonProps} session={sessionForPages} />}
            {view === 'queue'   && <Queue {...commonProps} session={sessionForPages} />}
            {view === 'del'     && <Delivery {...commonProps} session={sessionForPages} />}
            {view === 'add'     && <AddVehicle {...commonProps} session={sessionForPages} />}
            {view === 'archive' && <Archive {...commonProps} session={sessionForPages} />}
            {view === 'users'   && <Users {...commonProps} session={session} />}
            {view === 'models'  && <CarModels />}
          </div>
        </div>
      </div>

      <VehicleModal
        open={vehicleModalId != null}
        vehicleId={vehicleModalId}
        session={sessionForPages}
        onClose={() => setVehicleModalId(null)}
        onSaved={bump}
        onArchived={bump}
        onDeleted={bump}
        onOpenChecklist={(type, vehicle) => setChecklistCtx({ type, vehicle })}
      />

      <ChecklistModal
        open={checklistCtx != null}
        type={checklistCtx?.type}
        vehicle={checklistCtx?.vehicle}
        onClose={() => setChecklistCtx(null)}
        onCompleted={bump}
      />

      <ToastHost />
    </>
  );
}
